// @ts-nocheck
import React, { createContext, useState, useContext, useRef, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import MessageHandler from '../utils/MessageHandler';
import HIDSender from '../utils/HIDSender';
import HIDTransportRegistry from '../utils/HIDTransportRegistry';
import DeviceDataStore from '../utils/DeviceDataStore';
import { parseCommandByte } from '../utils/ProtocolHeader';
import { recordProtocol } from '../utils/ProtocolLog';
import {
  getDeviceModel,
  getReceiverModel,
  getReceiverStatePresentation,
  getWiredDeviceCardState,
  isBluetoothReportedName,
  isLegacyDpiColorModel,
  isReceiverTransport,
  mergeReceiverCardState,
  resolveModelDisplayName,
} from '../utils/DeviceCatalog';
import { resolveAdapter } from '../utils/AdapterRegistry';
import {
  deviceKey,
  formatDeviceMac,
  historyCardMac,
  mergeDeviceHistory,
  removeFromDeviceHistory,
  upsertDeviceHistory,
} from '../utils/DeviceIdentity';
import {
  isRtGearSample,
  isRtMonitorPacket,
  isRtMonitorSample,
} from '../utils/RtMonitor';
import { isRtCalibratePacket } from '../utils/RtCalibrate';
import { isRtCalibrateConfigPacket } from '../utils/RtCalibrateConfig';
import {
  LIGHTING_EFFECT_SWITCH_TYPE,
  LIGHTING_EFFECT_WRITE_TYPE,
  LIGHTING_HEADER_SIZE,
  LIGHTING_MAX_PACKETS,
  LIGHTING_READ_TYPE,
  LIGHTING_WRITE_TYPE,
  isLastLightingPacket,
  lightingPacketPayload,
  mergeLightingPackets,
} from '../utils/LightingProtocol';

const DeviceContext = createContext()

/** 已授权设备的使用历史（localStorage），不代表设备当前在线 */
const PAIRED_DEVICES_KEY = 'mouse-driver.pairedDevices';
const REMOVED_PAIRED_DEVICES_KEY = 'mouse-driver.removedPairedDevices';

/** 多包宏读取停滞多久后，新到的第0包按新一轮读取处理而不是重复包 */
const MACRO_READ_STALL_MS = 1500;

/**
 * 请求下一个宏数据包前的间隔。无线接收器刚回完上一包就立刻（0~2ms）收到
 * 续包请求时，偶发不应答，且此后对同一索引的重发也全部沉默（读取状态机
 * 疑似被打断）；留出间隔可避免触发该状态。
 */
const MACRO_INTER_PACKET_DELAY_MS = 20;

/**
 * 单包重发耗尽后整轮重读（从第0包重新开始）的最大次数与重读前间隔。
 * 经验规律：类型1 首包请求总能得到应答，而被打断后的续包索引请求会一直
 * 沉默，因此整轮重读是唯一可靠的恢复手段。
 */
const MACRO_READ_MAX_RESTARTS = 2;
const MACRO_READ_RESTART_DELAY_MS = 250;

/** 驱动界面被挂起（设备休眠/无信号）后，轮询接收器状态等待唤醒的间隔 */
const SUSPEND_POLL_INTERVAL_MS = 3000;

/** 挂起期间轮询接收器状态的超时时间。设备休眠时接收器通常直接不应答 */
const SUSPEND_POLL_TIMEOUT_MS = 900;

const HID_FILTERS = [
  { usagePage: 0xff60, usage: 0x61 }, // keyboard / QMK RAW
  { usagePage: 0xff0a, usage: 0x01 }, // mouse vendor
  { usagePage: 0xff00, usage: 0x01 }, // generic vendor
  { usagePage: 0x8c, usage: 0x01 },   // DFU / receiver
];

/** 计算 report items 字节数 */
function reportItemsBytes(items = []) {
  const bits = items.reduce((sum, item) => {
    return sum + (item.reportCount || 0) * (item.reportSize || 0);
  }, 0);
  return Math.ceil(bits / 8);
}

/**
 * 评估某个 HIDDevice 是否更像“配置通道”
 * 优先：厂商 page + 64 字节 output
 */
function scoreHidInterface(device) {
  if (!device) return -1;
  let score = 0;
  const collections = device.collections || [];
  let maxOut = 0;
  let hasVendor = false;
  let has64Out = false;

  for (const c of collections) {
    const page = Number(c.usagePage) || 0;
    if (page >= 0xff00 && page <= 0xffff) {
      hasVendor = true;
      score += 40;
      if (page === 0xff60 || page === 0xff0a) score += 15;
    }
    // 标准鼠标/键盘接口降权
    if (page === 0x01 || page === 0x07 || page === 0x0c) score -= 20;

    for (const r of c.outputReports || []) {
      const bytes = reportItemsBytes(r.items || []);
      maxOut = Math.max(maxOut, bytes);
      if (bytes === 64) {
        has64Out = true;
        score += 80;
      } else if (bytes >= 32) {
        score += 25;
      } else if (bytes > 0) {
        score += 5;
      }
    }
    for (const r of c.featureReports || []) {
      const bytes = reportItemsBytes(r.items || []);
      if (bytes === 64) score += 20;
    }
  }

  if (hasVendor && has64Out) score += 50;
  if (maxOut === 0) score -= 100; // 无 output 基本不能配置
  return score;
}

/**
 * 从候选列表中挑最适合驱动协议的接口
 * @param {HIDDevice[]} devices
 * @param {{vendorId?:number, productId?:number}} [prefer]
 */
function pickBestHidInterface(devices, prefer = {}) {
  if (!devices?.length) return null;
  let list = [...devices];
  if (prefer.vendorId != null && prefer.productId != null) {
    const filtered = list.filter(
      (d) =>
        Number(d.vendorId) === Number(prefer.vendorId) &&
        Number(d.productId) === Number(prefer.productId)
    );
    if (!filtered.length) return null;
    list = filtered;
  }

  list.sort((a, b) => scoreHidInterface(b) - scoreHidInterface(a));
  const best = list[0];
  console.log(
    'HID interface candidates:',
    list.map((d) => ({
      name: d.productName,
      vid: d.vendorId,
      pid: d.productId,
      score: scoreHidInterface(d),
      collections: (d.collections || []).map((c) => ({
        usagePage: '0x' + Number(c.usagePage || 0).toString(16),
        usage: '0x' + Number(c.usage || 0).toString(16),
        outs: (c.outputReports || []).map((r) => ({
          id: r.reportId,
          size: reportItemsBytes(r.items || []),
        })),
      })),
    }))
  );
  console.log('Picked HID interface score=', scoreHidInterface(best), best?.productName);
  return best;
}

function loadPairedDevices() {
  try {
    const raw = localStorage.getItem(PAIRED_DEVICES_KEY);
    if (!raw) return [];
    const list = JSON.parse(raw);
    return Array.isArray(list) ? list : [];
  } catch {
    return [];
  }
}

function savePairedDevices(list) {
  try {
    localStorage.setItem(PAIRED_DEVICES_KEY, JSON.stringify(list));
  } catch (e) {
    console.warn('保存配对设备失败:', e);
  }
}

function loadRemovedPairedDeviceKeys() {
  try {
    const raw = localStorage.getItem(REMOVED_PAIRED_DEVICES_KEY);
    if (!raw) return [];
    const list = JSON.parse(raw);
    return Array.isArray(list) ? list : [];
  } catch {
    return [];
  }
}

function saveRemovedPairedDeviceKeys(list) {
  try {
    localStorage.setItem(
      REMOVED_PAIRED_DEVICES_KEY,
      JSON.stringify([...new Set(list)])
    );
  } catch (e) {
    console.warn('保存已删除设备列表失败:', e);
  }
}

/**
 * 留下浏览器已授权、且当前仍连接的设备。
 * 同一 VID/PID 里已有可配置接口时，只留下这些接口，避免普通鼠标端点再占一张卡片。
 * 如果授权接口都还看不出 Output Report，这些已插上的设备仍然全部保留。
 */
export function selectGrantedDevices(grantedDevices, removedDeviceKeys = []) {
  const removedKeys = new Set(removedDeviceKeys);
  const groups = new Map();

  for (const granted of grantedDevices || []) {
    if (!granted) continue;
    const key = deviceKey(granted.vendorId, granted.productId);
    if (removedKeys.has(key)) continue;
    const group = groups.get(key);
    if (group) group.push(granted);
    else groups.set(key, [granted]);
  }

  const selected = [];
  for (const group of groups.values()) {
    const configurable = group.filter((device) => scoreHidInterface(device) >= 0);
    selected.push(...(configurable.length ? configurable : group));
  }
  return selected;
}

function getAuthorizedTransportDevices(
  grantedDevices,
  history,
  getTransportId,
  removedDeviceKeys = []
) {
  const historyByKey = new Map(
    history.map((item) => [
      deviceKey(item.vendorId, item.productId),
      item,
    ])
  );
  const entries = [];

  for (const granted of selectGrantedDevices(grantedDevices, removedDeviceKeys)) {
    const key = deviceKey(granted.vendorId, granted.productId);
    const remembered = historyByKey.get(key);
    const receiver = getReceiverModel(granted.vendorId, granted.productId);
    const wiredState = receiver
      ? null
      : getWiredDeviceCardState(
          granted.vendorId,
          granted.productId,
          granted.productName
        );
    const candidate = {
      transportId: getTransportId(granted),
      hidDevice: granted,
      vendorId: granted.vendorId,
      productId: granted.productId,
      productName:
        wiredState?.productName ||
        receiver?.name ||
        granted.productName ||
        '已授权设备',
      image: wiredState?.image || null,
      connectionType: receiver
        ? 'wireless'
        : wiredState?.connectionKind === 'bluetooth'
          ? 'bluetooth'
          : 'wired',
      isReceiver: Boolean(receiver),
      receiverInfo: receiver,
      targetInfo: wiredState?.targetInfo || null,
      receiverState: receiver ? null : 1,
      receiverTimedOut: false,
      targetOnline: receiver ? false : wiredState.targetOnline,
      targetVendorId: receiver ? null : wiredState.targetVendorId,
      targetProductId: receiver ? null : wiredState.targetProductId,
      accessible: receiver ? false : wiredState.accessible,
      statusKey: receiver ? 'checking' : wiredState.statusKey,
      statusLabel: receiver ? '检测中' : wiredState.statusLabel,
      statusMessage: receiver
        ? '正在检测接收器设备…'
        : wiredState.statusMessage,
      lastConnectedAt: remembered?.lastConnectedAt || 0,
    };
    entries.push(candidate);
  }

  return entries.sort(
    (a, b) =>
      (b.lastConnectedAt || 0) - (a.lastConnectedAt || 0) ||
      scoreHidInterface(b.hidDevice) - scoreHidInterface(a.hidDevice)
  );
}

/**
 * 串行拉取一台设备的完整配置数据集。
 * 首次连接成功后与休眠唤醒后都走这里，保证两条路径读到的数据一致。
 */
async function fetchDeviceDataset(sender, dataStore, model, adapter) {
  await sender.requestDeviceConfig();
  await sender.requestInformationData(sender.requestTimeout);
  await sender.requestMouseButtonData();
  await sender.requestPerformanceData(1);
  if (!dataStore.isProtocolAtLeast(4, 0) && isLegacyDpiColorModel(model)) {
    await sender.requestColorData(1);
  } else if (adapter?.capabilities?.lightingSettings) {
    // 带灯机型：command=5/type=1 读取全部灯效数据
    await sender.getLightingData();
  }
  await sender.requestPerformanceData(3);
  try {
    await sender.getMacroData(1);
  } catch (macroErr) {
    // 宏读取失败不应让其它设备功能也连接失败；后续进入宏页或保存时
    // 仍可再次发起读取。
    console.error('自动获取宏数据失败:', macroErr);
  }
}

export const useDevice = () => {
  const context = useContext(DeviceContext);
  if (!context) {
    throw new Error('useDevice must be used within DeviceProvider');
  }
  return context;
};

export const DeviceProvider = ({ children }) => {
  const navigate = useNavigate();
  // HashRouter（声明式模式）下 useNavigate 每次路由变化都返回新函数；
  // 它若直接进入 useCallback 依赖，会让 clearActiveTarget →
  // handleTransportInput → refreshOnlinePairedDevices 整条链在每次导航后
  // 换身份，进而触发下方 useEffect 的 cleanup 把所有 inputreport 监听器
  // 拆掉——宏多包读取的响应就在这个空窗期里丢失。必须用 ref 隔离。
  const navigateRef = useRef(navigate);
  useEffect(() => {
    navigateRef.current = navigate;
  }, [navigate]);
  const [device, setDevice] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [connectionPhase, setConnectionPhase] = useState('idle');
  const [transportConnected, setTransportConnected] = useState(false);
  const [connectionType, setConnectionType] = useState(null);
  const [receiverState, setReceiverState] = useState(null);
  const [targetOnline, setTargetOnline] = useState(false);
  const [targetVid, setTargetVid] = useState(null);
  const [targetPid, setTargetPid] = useState(null);
  const [activeAdapter, setActiveAdapter] = useState(null);
  const [driverReady, setDriverReady] = useState(false);
  // 已进入驱动界面的无线设备休眠/失联时，会话被挂起（保留驱动界面 + 蒙版提示），
  // 而不是直接退回设备选择页。
  const [deviceSuspended, setDeviceSuspended] = useState(false);
  const [suspendNotice, setSuspendNotice] = useState(null);
  const [deviceInfo, setDeviceInfo] = useState(null);
  const [protocolVersion, setProtocolVersion] = useState(null);
  const [error, setError] = useState(null);
  const [isConnecting, setIsConnecting] = useState(false);
  // 已授权 USB 设备列表。接收器在对应鼠标离线时仍保留并展示卡片，
  // 状态为「无线设备未连接」，鼠标上线后再换成机型。
  const [pairedDevices, setPairedDevices] = useState([]);
  // 本地连接历史（按 MAC 区分同型号设备），用于展示当前未授权/未插入的设备
  const [deviceHistory, setDeviceHistory] = useState(() => loadPairedDevices());
  // 已读到的传输设备 MAC；null 表示读过但设备没有有效 MAC
  const transportMacsRef = useRef(new WeakMap());
  const messageHandlerRef = useRef(null);
  const senderRef = useRef(null);
  const dataStoreRef = useRef(null);
  const deviceRef = useRef(null);
  const transportInputHandlersRef = useRef(new Map());
  const transportRegistryRef = useRef(new HIDTransportRegistry());
  const receiverProbePromisesRef = useRef(new Map());
  const connectingRef = useRef(false);
  const connectionAttemptRef = useRef(0);
  // 挂起/唤醒流程在 inputreport 回调里执行，必须走 ref 读取最新状态，
  // 否则会依赖 state 触发回调重建（那会拆掉监听器，见下方 navigateRef 说明）。
  const suspendedRef = useRef(false);
  const resumingRef = useRef(false);
  const driverReadyRef = useRef(false);
  const deviceInfoRef = useRef(null);
  const activeAdapterRef = useRef(null);

  useEffect(() => {
    driverReadyRef.current = driverReady;
  }, [driverReady]);
  useEffect(() => {
    deviceInfoRef.current = deviceInfo;
  }, [deviceInfo]);
  useEffect(() => {
    activeAdapterRef.current = activeAdapter;
  }, [activeAdapter]);

  // 宏数据多包接收缓冲
  const macroPacketBufferRef = useRef({
    packets: [],           // 存储每个包的数据
    expectedPackets: 0,    // 预期包数
    totalLength: 0,        // 总数据长度
    lastPacketAt: 0,       // 最近一个包的到达时间，用于识别已停滞的读取
    restartCount: 0        // 本轮读取已整轮重启的次数
  });

  // 灯光数据多包接收缓冲（command=5/type=1）
  const lightingPacketBufferRef = useRef({
    payloads: [],      // 按包索引存放各包 payload
    firstPacket: null  // 第 0 包，合并时用来取包头
  });

  // 初始化数据存储
  if (!dataStoreRef.current) {
    dataStoreRef.current = new DeviceDataStore();
    console.log('数据存储已初始化');
  }

  // 初始化消息处理器
  if (!messageHandlerRef.current) {
    messageHandlerRef.current = new MessageHandler();
    
    // 注册消息监听器 - 将接收到的数据保存到数据存储
    messageHandlerRef.current.on('message', (data) => {
      // 根据命令类型保存到数据存储。0xF1 监测推送很密，不打印
      const response = data.raw;
      if (!isRtMonitorPacket(response)) {
        console.log('Message received:', data);
      }
      if (response && response.length > 0) {
        const command =
          data?.parsed?.command ?? parseCommandByte(response[0]).command;
        
        switch (command) {
          case 0:
            // 设备配置数据
            console.log('保存设备配置数据');
            dataStoreRef.current.setDeviceConfig(response);
            break;
          case 1:
            // 通告信息
            console.log('保存通告信息');
            dataStoreRef.current.setInformationData(response);
            break;
          case 3:
            // 鼠标按键信息
            console.log('保存鼠标按键信息');
            dataStoreRef.current.setMouseButtonData(response);
            break;
          case 4:
            if (isRtMonitorSample(response)) {
              dataStoreRef.current.setRtMonitorSample(response);
              break;
            }
            if (isRtGearSample(response)) {
              dataStoreRef.current.setRtGearSample(response);
              break;
            }
            if (isRtCalibratePacket(response)) {
              break;
            }
            if (isRtCalibrateConfigPacket(response)) {
              break;
            }
            // 设备性能数据
            console.log('保存设备性能数据');
            dataStoreRef.current.setPerformanceData(response);
            break;
          case 5: {
            // 颜色 / 灯光数据。灯光 payload 最长 87 字节，设备会像宏一样分包
            // 回应；byte2 的 bit7 表示这是最后一包。
            const lightingBuffer = lightingPacketBufferRef.current;
            const packetIndex = response[1];
            const isLightingRead = response[3] === LIGHTING_READ_TYPE;
            const isLast = isLastLightingPacket(response);

            // 写入灯光时设备也用命令5逐包回应（type=2 整包 / type=4 单独设置
            // 灯效）。这些回包不是读取结果，它们的 payload 只有本次写的那点
            // 内容，整包存进 colorData 会把已存的完整灯光数据换掉。
            if (
              response[3] === LIGHTING_WRITE_TYPE ||
              response[3] === LIGHTING_EFFECT_WRITE_TYPE
            ) {
              console.log('忽略灯光写入回包（非读取结果）');
              break;
            }

            // 切换灯效（type=5）的回包 byte4 是新的 effect 序号。按设备的做法
            // 只拿它替换已存数据里的 effect，后面的字节（各组 effect_info_t、
            // effectSleepTime、档位表）保持不变，而不是整包覆盖。
            if (response[3] === LIGHTING_EFFECT_SWITCH_TYPE) {
              if (response.length > LIGHTING_HEADER_SIZE) {
                console.log(`灯效切换回包：effect=${response[4]}，只更新 effect`);
                dataStoreRef.current.applyLightingEffectSwitch(response[4]);
              }
              break;
            }

            // 单包就结束的响应（旧机型的 DPI 档位颜色等）按原样入库
            if (!isLightingRead || (packetIndex === 0 && isLast)) {
              lightingBuffer.payloads = [];
              lightingBuffer.firstPacket = null;
              console.log('保存设备颜色数据');
              dataStoreRef.current.setColorData(response);
              break;
            }

            if (packetIndex === 0) {
              lightingBuffer.payloads = [];
              lightingBuffer.firstPacket = response;
            } else if (!lightingBuffer.firstPacket) {
              console.warn(`忽略没有首包的灯光数据包: ${packetIndex}`);
              break;
            }
            lightingBuffer.payloads[packetIndex] = lightingPacketPayload(response);

            const collected = lightingBuffer.payloads.filter(
              (chunk) => chunk !== undefined
            );
            if (isLast || packetIndex + 1 >= LIGHTING_MAX_PACKETS) {
              console.log(`灯光数据已收齐 ${collected.length} 个包，合并入库`);
              dataStoreRef.current.setColorData(
                mergeLightingPackets(collected, lightingBuffer.firstPacket)
              );
              lightingBuffer.payloads = [];
              lightingBuffer.firstPacket = null;
              break;
            }

            // 还没收完，继续请求下一包
            const lightingSender = senderRef.current;
            if (lightingSender) {
              const nextIndex = packetIndex + 1;
              console.log(`请求下一个灯光数据包: ${nextIndex}`);
              (async () => {
                await lightingSender.wait(MACRO_INTER_PACKET_DELAY_MS);
                await lightingSender.getLightingDataByIndex(nextIndex);
              })().catch((err) => {
                console.error(`请求灯光数据包 ${nextIndex} 失败:`, err);
                lightingBuffer.payloads = [];
                lightingBuffer.firstPacket = null;
              });
            }
            break;
          }
          case 7:
            // 宏数据 - 多包接收处理
            const macroBuffer = macroPacketBufferRef.current;
            const packetIndex = response[1];  // 包索引
            const responseType = response[3]; // 1=读取宏数据, 2=写入宏数据
            const dataPayload = response.slice(4, 63);  // 提取索引4-62的数据（59字节）

            // 写入宏数据时设备也用命令7逐包回应（setMacroData 会等这些回应，
            // 回包沿用请求头里的 type=2）。这些回包不是读取结果，混进多包缓冲
            // 会占住第0包，让紧接着那次回读被当成重复丢掉，界面于是停在写入前
            // 的旧宏内容上。
            if (responseType === 2) {
              console.log('忽略宏写入回包（非读取结果）');
              break;
            }

            console.log(`收到宏数据包 ${packetIndex}, 数据长度: ${dataPayload.length}`);

            // 第0包的长度字段为 0xFFFF 时，设备表示当前没有板载宏。
            // 不能将其当作 65535 字节处理，否则会继续误请求 1110 个数据包。
            if (
              packetIndex === 0 &&
              response[4] === 0xFF &&
              response[5] === 0xFF
            ) {
              console.log('设备没有板载宏');
              macroBuffer.packets = [];
              macroBuffer.expectedPackets = 0;
              macroBuffer.totalLength = 0;
              macroBuffer.lastPacketAt = 0;
              macroBuffer.restartCount = 0;

              // 发布空宏状态，让上层移除旧板载宏并保留本地宏。
              dataStoreRef.current.setMacroData(response);
              break;
            }

            // 重试后迟到的旧响应可能与新响应重复到达。已收过的包不再
            // 重置缓存或继续触发下一包，避免同一索引出现并发请求。
            // 例外：上一次读取早已停滞（设备中途不再回包）时缓冲里会一直
            // 留着第0包，此时必须让新读取的第0包重新开始，否则之后所有宏
            // 读取都会被当成重复丢掉，界面永远停在旧数据上。
            if (macroBuffer.packets[packetIndex]) {
              const stalled =
                packetIndex === 0 &&
                Date.now() - macroBuffer.lastPacketAt > MACRO_READ_STALL_MS;
              if (!stalled) {
                console.log(`忽略重复的宏数据包: ${packetIndex}`);
                break;
              }
              console.log('上一次宏读取已停滞，按新的读取重新开始');
            }

            // 第一个包：读取总长度，计算预期包数
            if (packetIndex === 0) {
              const dataLength = (response[5] << 8) | response[4];  // 数据总长度（2字节小端）
              macroBuffer.totalLength = dataLength;
              macroBuffer.expectedPackets = Math.ceil(dataLength / 59);
              macroBuffer.packets = [];  // 清空之前的缓存
              console.log(`宏数据总长度: ${dataLength}, 预计需要 ${macroBuffer.expectedPackets} 个包`);
            } else if (
              macroBuffer.expectedPackets === 0 ||
              packetIndex >= macroBuffer.expectedPackets
            ) {
              console.warn(`忽略无效的宏数据包索引: ${packetIndex}`);
              break;
            }

            // 存储当前包数据
            macroBuffer.packets[packetIndex] = {
              index: packetIndex,
              data: dataPayload,
              fullResponse: response
            };
            macroBuffer.lastPacketAt = Date.now();

            // 检查是否收齐所有包
            const receivedCount = macroBuffer.packets.filter(p => p !== undefined).length;
            console.log(`已收到 ${receivedCount}/${macroBuffer.expectedPackets} 个宏数据包`);

            // 如果还有更多包需要请求，主动请求下一个包
            if (receivedCount < macroBuffer.expectedPackets && senderRef.current && response[3] === 1) {
              let nextPacketIndex = 0;
              while (
                nextPacketIndex < macroBuffer.expectedPackets &&
                macroBuffer.packets[nextPacketIndex]
              ) {
                nextPacketIndex += 1;
              }
              console.log(`请求下一个宏数据包: ${nextPacketIndex}`);
              const macroSender = senderRef.current;
              (async () => {
                await macroSender.wait(MACRO_INTER_PACKET_DELAY_MS);
                await macroSender.getMacroDataByIndex(nextPacketIndex);
              })().catch((err) => {
                // 单包重发耗尽：这次读取已经不可能收齐，留着半份缓冲只会让
                // 后续读取的第0包被当成重复包丢掉，先清空。
                console.error(`请求宏数据包 ${nextPacketIndex} 失败:`, err);
                macroBuffer.packets = [];
                macroBuffer.expectedPackets = 0;
                macroBuffer.totalLength = 0;
                macroBuffer.lastPacketAt = 0;

                // 被打断后的续包索引请求会一直沉默，继续重发同一索引没有
                // 意义；整轮从第0包重读（类型1首包请求总能得到应答）。
                const restarts = macroBuffer.restartCount || 0;
                if (restarts >= MACRO_READ_MAX_RESTARTS) {
                  console.error('宏读取整轮重试次数已用尽，放弃本次宏读取');
                  recordProtocol(
                    'error',
                    `宏读取整轮重试 ${MACRO_READ_MAX_RESTARTS} 次后仍失败，放弃`
                  );
                  macroBuffer.restartCount = 0;
                  return;
                }
                macroBuffer.restartCount = restarts + 1;
                console.warn(
                  `整轮重新读取宏数据 (${macroBuffer.restartCount}/${MACRO_READ_MAX_RESTARTS})`
                );
                recordProtocol(
                  'event',
                  `宏读取整轮重启 (${macroBuffer.restartCount}/${MACRO_READ_MAX_RESTARTS})`
                );
                (async () => {
                  await macroSender.wait(MACRO_READ_RESTART_DELAY_MS);
                  await macroSender.getMacroData(1);
                })().catch((restartErr) => {
                  console.error('宏读取整轮重启失败，放弃本次宏读取:', restartErr);
                  recordProtocol(
                    'error',
                    `宏读取整轮重启失败: ${restartErr?.message || restartErr}`
                  );
                  macroBuffer.restartCount = 0;
                });
              });
            }

            if (receivedCount === macroBuffer.expectedPackets && macroBuffer.expectedPackets > 0) {
              // 合并所有包的数据
              console.log('所有宏数据包已收到，开始合并数据...');

              // 创建合并后的数组：前4字节（头部）+ 所有包的数据
              const mergedData = new Uint8Array(4 + macroBuffer.expectedPackets * 59);

              // 复制第一个包的前4字节（头部信息）
              for (let i = 0; i < 4; i++) {
                mergedData[i] = macroBuffer.packets[0].fullResponse[i];
              }
              mergedData[1] = 0;  // 索引重置为0

              // 按索引顺序合并所有包的数据（从第4字节开始）
              for (let i = 0; i < macroBuffer.expectedPackets; i++) {
                if (macroBuffer.packets[i]) {
                  mergedData.set(macroBuffer.packets[i].data, 4 + i * 59);
                }
              }

              console.log('合并后的宏数据长度:', mergedData.length);
              console.log('合并后数据 (前50字节):', Array.from(mergedData.slice(0, 50)).map(b => b.toString(16).padStart(2, '0')).join(' '));

              // 保存合并后的数据
              dataStoreRef.current.setMacroData(mergedData);

              // 清空缓存；本轮读取成功，整轮重试预算恢复
              macroBuffer.packets = [];
              macroBuffer.expectedPackets = 0;
              macroBuffer.totalLength = 0;
              macroBuffer.lastPacketAt = 0;
              macroBuffer.restartCount = 0;
            }
            break;
          default:
            console.log(`未处理的命令类型: ${command}`);
        }
      }
    });

    messageHandlerRef.current.on('config_response', (data) => {
      console.log('Config response:', data);
    });

    messageHandlerRef.current.on('status_update', (data) => {
      // 0xF1 监测推送很密，不打印
      if (isRtMonitorPacket(data?.raw)) return;
      console.log('Status update:', data);
    });
  }

  const getTransportId = useCallback((hidDevice) => {
    return transportRegistryRef.current.getId(hidDevice);
  }, []);

  const setTransportMac = useCallback((hidDevice, mac) => {
    if (!hidDevice) return;
    const value = mac || null;
    transportMacsRef.current.set(hidDevice, value);
    const transportId = getTransportId(hidDevice);
    setPairedDevices((prev) =>
      prev.map((entry) =>
        entry.transportId === transportId && entry.mac !== value
          ? { ...entry, mac: value }
          : entry
      )
    );
  }, [getTransportId]);

  const rememberDevice = useCallback((hidDevice, info) => {
    const historyInfo = {
      vendorId: info.vendorId,
      productId: info.productId,
      mac: info.mac || null,
      productName: info.productName || hidDevice?.productName || '已授权设备',
      image: info.image || null,
      connectionType: info.connectionType || null,
    };
    const history = upsertDeviceHistory(loadPairedDevices(), historyInfo);
    savePairedDevices(history);
    setDeviceHistory(history);
    const key = deviceKey(info.vendorId, info.productId);
    saveRemovedPairedDeviceKeys(
      loadRemovedPairedDeviceKeys().filter((removedKey) => removedKey !== key)
    );
  }, []);

  const removePairedDevice = useCallback(async (
    transportId,
    vendorId,
    productId
  ) => {
    const entry = pairedDevices.find(
      (pairedDevice) => pairedDevice.transportId === transportId
    );
    const mac = entry ? entry.mac || null : historyCardMac(transportId);
    const history = removeFromDeviceHistory(loadPairedDevices(), {
      vendorId,
      productId,
      mac,
    });
    savePairedDevices(history);
    setDeviceHistory(history);

    // 历史卡片没有浏览器授权可撤销，删掉记录即可
    if (!entry) return;

    const key = deviceKey(vendorId, productId);

    // 新版 Chromium 支持主动撤销 WebHID 授权；旧版浏览器则依靠本地
    // 删除列表避免下次刷新时重新展示，用户重新添加设备后会自动恢复。
    try {
      if (typeof entry?.hidDevice?.forget === 'function') {
        await entry.hidDevice.forget();
      }
    } catch (forgetError) {
      console.warn('撤销设备授权失败，将仅从设备列表隐藏:', forgetError);
    }

    setPairedDevices((prev) => {
      const next = prev.filter(
        (d) => d.transportId !== transportId
      );
      return next;
    });
    saveRemovedPairedDeviceKeys([
      ...loadRemovedPairedDeviceKeys().filter(
        (removedKey) => removedKey !== key
      ),
      key,
    ]);
  }, [pairedDevices]);

  const updateReceiverListEntry = useCallback((hidDevice, status, timedOut = false) => {
    const transportId = getTransportId(hidDevice);
    // 接收器的 MAC 属于它当前连着的鼠标；鼠标离线后下次可能换成另一台
    const targetGone = status != null && status.deviceState !== 1;
    if (targetGone) transportMacsRef.current.delete(hidDevice);
    setPairedDevices((prev) =>
      prev.map((entry) => {
        if (entry.transportId !== transportId) return entry;
        const merged = mergeReceiverCardState(entry, status, timedOut);
        return targetGone ? { ...merged, mac: null } : merged;
      })
    );
  }, [getTransportId]);

  const updatePairedDeviceBattery = useCallback((hidDevice, response) => {
    const raw = response instanceof Uint8Array
      ? response
      : new Uint8Array(response || []);
    const level = Number(raw[11]);
    if (raw.length < 12 || !Number.isFinite(level) || level < 0 || level > 100) {
      return;
    }
    const transportId = getTransportId(hidDevice);
    setPairedDevices((prev) =>
      prev.map((entry) =>
        entry.transportId === transportId
          ? {
              ...entry,
              batteryLevel: level,
              charging: Number(raw[10]) === 1,
            }
          : entry
      )
    );
  }, [getTransportId]);

  const clearActiveTarget = useCallback((
    message,
    phase = 'error',
    nextReceiverState = null
  ) => {
    connectionAttemptRef.current += 1;
    senderRef.current?.setDeviceAccessAllowed(false);
    if (senderRef.current) senderRef.current.protocolVersion = null;
    messageHandlerRef.current?.clearBuffer();
    dataStoreRef.current?.clear();
    macroPacketBufferRef.current = {
      packets: [],
      expectedPackets: 0,
      totalLength: 0,
      lastPacketAt: 0,
      restartCount: 0,
    };
    lightingPacketBufferRef.current = { payloads: [], firstPacket: null };
    suspendedRef.current = false;
    resumingRef.current = false;
    setDeviceSuspended(false);
    setSuspendNotice(null);
    setReceiverState(nextReceiverState);
    setTargetOnline(false);
    setTargetVid(null);
    setTargetPid(null);
    setActiveAdapter(null);
    setDriverReady(false);
    setIsConnected(false);
    setConnectionPhase(phase);
    setDeviceInfo(null);
    setProtocolVersion(null);
    setError(message || null);
    // 经 navigateRef 调用以保持本回调身份稳定（见 navigateRef 处说明）
    navigateRef.current('/', { replace: true });
  }, []);

  /**
   * 挂起当前驱动会话：设备休眠或长时间无信号时保留驱动界面与已读数据，
   * 只禁止继续下发命令，并交给上层弹出蒙版提示。
   */
  const suspendActiveTarget = useCallback((presentation, state) => {
    senderRef.current?.setDeviceAccessAllowed(false);
    messageHandlerRef.current?.clearBuffer();
    // 挂起瞬间多半有一轮读取被打断，留着半份缓冲会让唤醒后的第0包被当成
    // 重复包丢掉。
    macroPacketBufferRef.current = {
      packets: [],
      expectedPackets: 0,
      totalLength: 0,
      lastPacketAt: 0,
      restartCount: 0,
    };
    lightingPacketBufferRef.current = { payloads: [], firstPacket: null };

    const notice = {
      state: state ?? null,
      statusKey: presentation?.key || 'offline',
      label: presentation?.label || '设备离线',
      message:
        presentation?.message ||
        '无线设备已断开，请唤醒设备或重新连接',
      resuming: false,
      resumeError: null,
    };

    if (!suspendedRef.current) {
      suspendedRef.current = true;
      recordProtocol('event', `设备离线（${notice.label}），挂起驱动界面`);
    }
    setDeviceSuspended(true);
    setSuspendNotice(notice);
    setIsConnected(false);
    setConnectionPhase('suspended');
  }, []);

  /**
   * 设备重新在线后恢复会话：重新允许下发命令并整体回读一次配置数据，
   * 避免休眠期间设备侧发生变化而界面还停在旧数据上。
   */
  const resumeActiveTarget = useCallback(async (status) => {
    if (!suspendedRef.current || resumingRef.current) return;
    const sender = senderRef.current;
    const dataStore = dataStoreRef.current;
    const info = deviceInfoRef.current;
    if (!sender || !dataStore || !info) return;

    // 唤醒后接收器对应的可能已经是另一台鼠标，旧会话的数据不能继续沿用。
    if (
      status?.targetVendorId != null &&
      status?.targetProductId != null &&
      (Number(status.targetVendorId) !== Number(info.vendorId) ||
        Number(status.targetProductId) !== Number(info.productId))
    ) {
      clearActiveTarget('接收器已连接到另一台设备，请重新选择设备', 'error', 1);
      return;
    }

    resumingRef.current = true;
    setSuspendNotice((prev) =>
      prev ? { ...prev, resuming: true, resumeError: null } : prev
    );

    try {
      sender.setDeviceAccessAllowed(true);
      const model = getDeviceModel(info.vendorId, info.productId);
      await fetchDeviceDataset(
        sender,
        dataStore,
        model,
        activeAdapterRef.current
      );
      suspendedRef.current = false;
      setDeviceSuspended(false);
      setSuspendNotice(null);
      setIsConnected(true);
      setConnectionPhase('ready');
      setError(null);
      recordProtocol('event', '设备已唤醒，驱动界面已恢复');
    } catch (resumeError) {
      console.error('设备唤醒后刷新数据失败:', resumeError);
      sender.setDeviceAccessAllowed(false);
      setSuspendNotice((prev) =>
        prev
          ? {
              ...prev,
              resuming: false,
              resumeError:
                resumeError?.message || '重新读取设备数据失败，请重试',
            }
          : prev
      );
    } finally {
      resumingRef.current = false;
    }
  }, [clearActiveTarget]);

  /**
   * 挂起期间主动查询一次接收器状态。设备唤醒时接收器多数会主动上报，
   * 但上报可能丢失，因此蒙版界面也按固定间隔轮询（并作为「立即重试」入口）。
   * @returns {Promise<boolean>} 设备是否已恢复在线
   */
  const probeSuspendedDevice = useCallback(async () => {
    const sender = senderRef.current;
    const hidDevice = deviceRef.current;
    if (!suspendedRef.current || resumingRef.current || !sender || !hidDevice) {
      return false;
    }
    try {
      const status = await sender.getReceiverStatus(SUSPEND_POLL_TIMEOUT_MS);
      if (!status) return false;
      updateReceiverListEntry(hidDevice, status, false);
      setReceiverState(status.deviceState);
      if (status.deviceState !== 1) {
        const presentation = getReceiverStatePresentation(status.deviceState);
        setSuspendNotice((prev) =>
          prev
            ? {
                ...prev,
                state: status.deviceState,
                statusKey: presentation.key,
                label: presentation.label,
                message: presentation.message,
              }
            : prev
        );
        return false;
      }
      setTargetOnline(true);
      setTargetVid(status.targetVendorId);
      setTargetPid(status.targetProductId);
      await resumeActiveTarget(status);
      return !suspendedRef.current;
    } catch (probeError) {
      // 设备仍在休眠时接收器不应答，属预期情况，继续等待下一次轮询
      console.warn('等待设备唤醒中，接收器状态查询未成功:', probeError);
      return false;
    }
  }, [resumeActiveTarget, updateReceiverListEntry]);

  const handleTransportInput = useCallback((hidDevice, event) => {
    const sourceDevice = event?.device || hidDevice;
    const reportData = event?.data;
    const raw =
      reportData?.buffer && typeof reportData.byteLength === 'number'
        ? new Uint8Array(
            reportData.buffer,
            reportData.byteOffset || 0,
            reportData.byteLength
          ).slice()
        : new Uint8Array(reportData || []);
    const header = raw.length > 0 ? parseCommandByte(raw[0]) : null;
    const transportId = getTransportId(sourceDevice);
    const collections = (sourceDevice?.collections || []).map((collection) => ({
      usagePage: Number(collection.usagePage) || 0,
      usage: Number(collection.usage) || 0,
    }));

    // 0xF1 监测推送很密，不打印
    if (!isRtMonitorPacket(raw)) {
      console.log('[HID] 收到数据包', {
        transportId,
        sourceMatchesListener: sourceDevice === hidDevice,
        productName: sourceDevice?.productName || '',
        vendorId: sourceDevice?.vendorId ?? null,
        productId: sourceDevice?.productId ?? null,
        collections,
        reportId: event?.reportId ?? null,
        command: header?.command ?? null,
        deviceType: header?.deviceType ?? null,
        bit7: header?.bit7 ?? null,
        type: raw[3] ?? null,
        deviceState: raw[4] ?? null,
        hex: Array.from(raw, (byte) =>
          byte.toString(16).padStart(2, '0').toUpperCase()
        ).join(' '),
        raw,
      });
    }

    const receiverTransport = isReceiverTransport(
      sourceDevice?.vendorId,
      sourceDevice?.productId
    );

    // 活动设备不再走选择页电量探测，其列表卡片电量由主流程拉取的
    // 通告信息（command=1/type=1）在这里顺带刷新。
    if (header?.command === 1 && raw[3] === 1) {
      updatePairedDeviceBattery(sourceDevice, raw);
    }

    const status = receiverTransport
      ? HIDSender.parseReceiverStatusResponse(raw)
      : null;

    // 记录每个接收包的路由去向，便于排查“包到了但没进解析”的情况
    recordProtocol(
      'rx',
      status
        ? '状态包（在 handleTransportInput 消费，不进 MessageHandler）'
        : deviceRef.current === sourceDevice
          ? '转发 MessageHandler'
          : '忽略（非活动设备）',
      raw
    );

    if (status) {
      updateReceiverListEntry(sourceDevice, status, false);
      if (deviceRef.current === sourceDevice) {
        setReceiverState(status.deviceState);
        setTargetOnline(status.deviceState === 1);
        if (status.deviceState === 1) {
          setTargetVid(status.targetVendorId);
          setTargetPid(status.targetProductId);
          // 设备重新上线：若驱动界面正被挂起，直接原地恢复
          if (suspendedRef.current) {
            resumeActiveTarget(status);
          }
        } else {
          const presentation = getReceiverStatePresentation(
            status.deviceState
          );
          if (driverReadyRef.current) {
            // 已经进入驱动界面：休眠/失联不再把用户踢回设备选择页，
            // 改为挂起会话并弹出蒙版提示，设备回来后自动恢复。
            suspendActiveTarget(presentation, status.deviceState);
          } else {
            clearActiveTarget(
              presentation.message,
              'error',
              status.deviceState
            );
          }
        }
      }
      return;
    }

    if (
      deviceRef.current === sourceDevice &&
      messageHandlerRef.current
    ) {
      messageHandlerRef.current.handleInputReport(event);
    }
  }, [
    clearActiveTarget,
    getTransportId,
    resumeActiveTarget,
    suspendActiveTarget,
    updatePairedDeviceBattery,
    updateReceiverListEntry,
  ]);

  const attachTransportListener = useCallback((hidDevice) => {
    if (!hidDevice || transportInputHandlersRef.current.has(hidDevice)) return;
    const handler = (event) => handleTransportInput(hidDevice, event);
    hidDevice.addEventListener('inputreport', handler);
    transportInputHandlersRef.current.set(hidDevice, handler);
  }, [handleTransportInput]);

  const detachTransportListener = useCallback((hidDevice) => {
    const handler = transportInputHandlersRef.current.get(hidDevice);
    if (!handler) return;
    try {
      hidDevice.removeEventListener('inputreport', handler);
    } catch (_) {
      /* ignore */
    }
    transportInputHandlersRef.current.delete(hidDevice);
  }, []);

  const probeDeviceBattery = useCallback(async (hidDevice, connectionType) => {
    // 正在连接/已连接的设备由主流程负责读取电量；探测命令会插进主 sender
    // 的协议事务（设备只应答最后一条命令），曾导致多包宏读取被抢响应而失败。
    if (!hidDevice || deviceRef.current === hidDevice) return null;
    try {
      if (!hidDevice.opened) await hidDevice.open();
      attachTransportListener(hidDevice);
      const batterySender = new HIDSender(hidDevice);
      batterySender.setConnectionType(connectionType);
      batterySender.setDeviceAccessAllowed(true);
      const response = await batterySender.requestInformationData(900);
      if (response) updatePairedDeviceBattery(hidDevice, response);
      if (!transportMacsRef.current.has(hidDevice)) {
        try {
          setTransportMac(hidDevice, await batterySender.requestDeviceMac(900));
        } catch (macError) {
          console.warn('读取选择页设备 MAC 失败:', macError);
        }
      }
      return response;
    } catch (batteryError) {
      console.warn('读取选择页设备电量失败:', batteryError);
      return null;
    }
  }, [attachTransportListener, setTransportMac, updatePairedDeviceBattery]);

  const probeReceiverDevice = useCallback(
    async (hidDevice) => {
      // 正在连接/已连接的接收器不再探测：主流程和状态 notify 已维护其
      // 卡片状态，探测只会打断主 sender 的读事务（例如多包宏读取）。
      if (!hidDevice || deviceRef.current === hidDevice) return null;
      const pendingProbe = receiverProbePromisesRef.current.get(hidDevice);
      if (pendingProbe) return pendingProbe;

      const probe = (async () => {
        try {
          if (!hidDevice.opened) await hidDevice.open();
          // 底层监听器贯穿选择页和驱动页，必须先监听再发查询包。
          attachTransportListener(hidDevice);
          const probeSender = new HIDSender(hidDevice);
          probeSender.setDeviceAccessAllowed(false);
          const status = await probeSender.getReceiverStatus(900);
          updateReceiverListEntry(hidDevice, status, status == null);
          if (status?.deviceState === 1) {
            await probeDeviceBattery(hidDevice, 'wireless');
          }
          return status;
        } catch (probeError) {
          console.warn('接收器状态检测失败:', probeError);
          updateReceiverListEntry(hidDevice, null, true);
          return null;
        }
      })();

      receiverProbePromisesRef.current.set(hidDevice, probe);
      try {
        return await probe;
      } finally {
        if (receiverProbePromisesRef.current.get(hidDevice) === probe) {
          receiverProbePromisesRef.current.delete(hidDevice);
        }
      }
    },
    [
      attachTransportListener,
      probeDeviceBattery,
      updateReceiverListEntry,
    ]
  );

  const refreshOnlinePairedDevices = useCallback(async () => {
    if (!('hid' in navigator)) {
      setPairedDevices([]);
      return;
    }

    try {
      const granted = await navigator.hid.getDevices();
      const history = loadPairedDevices();
      setDeviceHistory(history);
      const entries = getAuthorizedTransportDevices(
        granted,
        history,
        getTransportId,
        loadRemovedPairedDeviceKeys()
      );
      setPairedDevices((prev) =>
        entries.map((entry) => {
          const existing = prev.find(
            (item) => item.transportId === entry.transportId
          );
          const mac = transportMacsRef.current.get(entry.hidDevice) ?? null;
          return existing
            ? {
                ...entry,
                ...existing,
                hidDevice: entry.hidDevice,
                transportId: entry.transportId,
                receiverInfo: entry.receiverInfo,
                mac,
              }
            : { ...entry, mac };
        })
      );

      const grantedSet = new Set(granted || []);
      for (const [hidDevice] of transportInputHandlersRef.current) {
        if (!grantedSet.has(hidDevice)) {
          detachTransportListener(hidDevice);
        }
      }

      await Promise.all(
        entries.map((entry) =>
          entry.isReceiver
            ? probeReceiverDevice(entry.hidDevice)
            : probeDeviceBattery(entry.hidDevice, 'wired')
        )
      );
    } catch (err) {
      console.warn('刷新在线设备列表失败:', err);
      setPairedDevices([]);
    }
  }, [
    detachTransportListener,
    getTransportId,
    probeDeviceBattery,
    probeReceiverDevice,
  ]);

  const openGrantedDevice = useCallback(async (selectedDevice) => {
    if (!selectedDevice) {
      throw new Error('未选择设备');
    }
    if (connectingRef.current) return false;
    connectingRef.current = true;
    setIsConnecting(true);
    setConnectionPhase('opening-hid');
    setError(null);
    setDriverReady(false);
    setIsConnected(false);
    setActiveAdapter(null);
    suspendedRef.current = false;
    resumingRef.current = false;
    setDeviceSuspended(false);
    setSuspendNotice(null);
    const attemptId = connectionAttemptRef.current + 1;
    connectionAttemptRef.current = attemptId;
    const ensureAttemptCurrent = () => {
      if (
        connectionAttemptRef.current !== attemptId ||
        deviceRef.current !== selectedDevice
      ) {
        const staleError = new Error('设备连接流程已失效');
        staleError.code = 'CONNECTION_SUPERSEDED';
        throw staleError;
      }
    };

    try {
      // 关闭旧连接
      if (deviceRef.current && deviceRef.current !== selectedDevice) {
        detachTransportListener(deviceRef.current);
        try {
          if (deviceRef.current.opened) await deviceRef.current.close();
        } catch (_) {
          /* ignore */
        }
      }

      if (!selectedDevice.opened) {
        await selectedDevice.open();
      }

      deviceRef.current = selectedDevice;
      attachTransportListener(selectedDevice);
      setDevice(selectedDevice);
      setTransportConnected(true);

      if (!senderRef.current) {
        senderRef.current = new HIDSender(selectedDevice);
      } else {
        senderRef.current.setDevice(selectedDevice);
      }

      const receiverTransport = isReceiverTransport(
        selectedDevice.vendorId,
        selectedDevice.productId
      );
      const nextConnectionType = receiverTransport ? 'wireless' : 'wired';
      setConnectionType(nextConnectionType);
      setConnectionPhase('discovering-device');
      senderRef.current.setConnectionType(nextConnectionType);
      senderRef.current.setDeviceAccessAllowed(!receiverTransport);

      try {
        const outs = senderRef.current.listOutputReports?.() || [];
        console.log('HID output reports:', outs);
        console.log(
          'preferred output size:',
          senderRef.current.getOutputReportSize?.(0)
        );
      } catch (e) {
        console.warn('无法读取 HID collections:', e);
      }

      let resolvedTargetVid = selectedDevice.vendorId;
      let resolvedTargetPid = selectedDevice.productId;

      if (receiverTransport) {
        const receiverStatus = await senderRef.current.getReceiverStatus(1000);
        ensureAttemptCurrent();
        updateReceiverListEntry(
          selectedDevice,
          receiverStatus,
          receiverStatus == null
        );
        if (!receiverStatus) {
          throw new Error('无法获取接收器状态，请重新插拔或重试');
        }
        setReceiverState(receiverStatus.deviceState);
        if (receiverStatus.deviceState !== 1) {
          throw new Error(
            getReceiverStatePresentation(receiverStatus.deviceState).message
          );
        }
        resolvedTargetVid = receiverStatus.targetVendorId;
        resolvedTargetPid = receiverStatus.targetProductId;
      } else {
        setReceiverState(null);
      }

      const model = getDeviceModel(resolvedTargetVid, resolvedTargetPid);
      if (!model) {
        throw new Error(
          `当前驱动不支持设备 VID 0x${Number(resolvedTargetVid)
            .toString(16)
            .padStart(4, '0')
            .toUpperCase()} / PID 0x${Number(resolvedTargetPid)
            .toString(16)
            .padStart(4, '0')
            .toUpperCase()}`
        );
      }

      setTargetVid(resolvedTargetVid);
      setTargetPid(resolvedTargetPid);
      setTargetOnline(true);
      senderRef.current.setDeviceAccessAllowed(true);

      const reportedName = receiverTransport
        ? ''
        : selectedDevice.productName || '';
      const displayName = resolveModelDisplayName(model, reportedName);
      const uiConnectionType =
        nextConnectionType === 'wireless'
          ? 'wireless'
          : isBluetoothReportedName(model, reportedName)
            ? 'bluetooth'
            : 'wired';
      setConnectionType(uiConnectionType);

      const devInfo = {
        modelId: model.id,
        productName: displayName,
        image: model.image,
        vendorId: resolvedTargetVid,
        productId: resolvedTargetPid,
        transportVendorId: selectedDevice.vendorId,
        transportProductId: selectedDevice.productId,
        connectionType: uiConnectionType,
      };
      setDeviceInfo(devInfo);
      deviceInfoRef.current = devInfo;
      dataStoreRef.current?.setDeviceInfo(devInfo);

      // 自动拉取设备数据（串行 + 间隔，避免忙碌写失败）
      try {
        // 第一条指令先查询驱动协议版本，后续再按对应协议拉取设备数据。
        setProtocolVersion(null);
        await senderRef.current.getProtocolVersion();
        ensureAttemptCurrent();
        const detectedVersion = senderRef.current.protocolVersion;
        setProtocolVersion(detectedVersion);
        dataStoreRef.current.setProtocolVersion(
          detectedVersion
        );
        setConnectionPhase('loading-adapter');
        const adapter = resolveAdapter(
          resolvedTargetVid,
          resolvedTargetPid,
          detectedVersion
        );
        if (!adapter) {
          throw new Error(
            `${model.name} 协议 ${detectedVersion || '未知'} 尚未适配`
          );
        }
        setActiveAdapter(adapter);
        activeAdapterRef.current = adapter;

        await fetchDeviceDataset(
          senderRef.current,
          dataStoreRef.current,
          model,
          adapter
        );
        ensureAttemptCurrent();
      } catch (fetchErr) {
        console.error('自动获取设备信息失败:', fetchErr);
        throw fetchErr;
      }

      const receiverModel = receiverTransport
        ? getReceiverModel(selectedDevice.vendorId, selectedDevice.productId)
        : null;
      const mac = formatDeviceMac(
        dataStoreRef.current.getData('deviceConfig')?.parsed?.mac
      );
      setTransportMac(selectedDevice, mac);
      rememberDevice(selectedDevice, {
        vendorId: selectedDevice.vendorId,
        productId: selectedDevice.productId,
        mac,
        productName: receiverModel?.name || displayName,
        image: receiverTransport ? null : model.image,
        connectionType: uiConnectionType,
      });
      ensureAttemptCurrent();
      setConnectionPhase('ready');
      setDriverReady(true);
      setIsConnected(true);
      navigate('/sensitivity');
      return true;
    } catch (err) {
      if (err?.code === 'CONNECTION_SUPERSEDED') {
        return false;
      }
      console.error('打开设备失败:', err);
      const msg = err?.message || String(err);
      // 用户取消选择不提示为错误
      if (!/not found|No device selected|The user canceled/i.test(msg)) {
        setError(msg.includes('cancel') || msg.includes('abort') ? null : msg);
      }
      senderRef.current?.setDeviceAccessAllowed(false);
      if (senderRef.current) senderRef.current.protocolVersion = null;
      messageHandlerRef.current?.clearBuffer();
      dataStoreRef.current?.clear();
      macroPacketBufferRef.current = {
        packets: [],
        expectedPackets: 0,
        totalLength: 0,
        lastPacketAt: 0,
        restartCount: 0,
      };
      lightingPacketBufferRef.current = { payloads: [], firstPacket: null };
      setIsConnected(false);
      setDriverReady(false);
      setActiveAdapter(null);
      setTargetOnline(false);
      setTargetVid(null);
      setTargetPid(null);
      setDeviceInfo(null);
      setConnectionPhase('error');
      setTransportConnected(Boolean(selectedDevice?.opened));
      setProtocolVersion(null);
      navigate('/', { replace: true });
      return false;
    } finally {
      connectingRef.current = false;
      setIsConnecting(false);
    }
  }, [
    attachTransportListener,
    detachTransportListener,
    rememberDevice,
    navigate,
    setTransportMac,
    updateReceiverListEntry,
  ]);

  /**
   * 弹出系统选择器并连接用户选择的设备。
   * 初始化成功后由 driverReady 状态直接切换到驱动界面。
   */
  const connectDevice = useCallback(async () => {
    if (!('hid' in navigator)) {
      setError('当前浏览器不支持 WebHID，请使用 Chrome / Edge / Opera');
      return false;
    }
    try {
      setError(null);
      // 不限制 filters 过死：用户可选中设备后，我们再从已授权接口里挑最佳配置通道
      const devices = await navigator.hid.requestDevice({
        filters: HID_FILTERS,
        // @ts-ignore chrome optional exclusionFilters not required
      });
      if (!devices || devices.length === 0) {
        return false;
      }

      // requestDevice 可能只返回用户点的那一个接口；再从 getDevices 拉全量同 VID/PID 接口择优
      const chosen = devices[0];
      const granted = await navigator.hid.getDevices();
      const best =
        pickBestHidInterface(granted, {
          vendorId: chosen.vendorId,
          productId: chosen.productId,
        }) || pickBestHidInterface(devices) || chosen;

      const opened = await openGrantedDevice(best);
      if (!opened) await refreshOnlinePairedDevices();
      return opened;
    } catch (err) {
      console.error('Connection error:', err);
      const msg = err?.message || String(err);
      if (!/canceled|abort|No device selected/i.test(msg)) {
        setError(msg);
      }
      return false;
    }
  }, [openGrantedDevice, refreshOnlinePairedDevices]);

  /** 连接已授权/已记录的设备（无需再次弹出选择器） */
  const connectPairedDevice = useCallback(
    async (transportId, vendorId, productId) => {
      if (!('hid' in navigator)) {
        setError('当前浏览器不支持 WebHID，请使用 Chrome / Edge / Opera');
        return false;
      }
      try {
        setError(null);
        const granted = await navigator.hid.getDevices();
        const knownEntry = pairedDevices.find(
          (entry) => entry.transportId === transportId
        );
        const exactDevice =
          knownEntry?.hidDevice && granted.includes(knownEntry.hidDevice)
            ? knownEntry.hidDevice
            : granted.find(
                (candidate) => getTransportId(candidate) === transportId
              );
        const best =
          exactDevice ||
          pickBestHidInterface(granted, { vendorId, productId });
        if (!best) {
          setError('浏览器尚未授权该设备，请点击「添加新设备」重新选择');
          return false;
        }
        return await openGrantedDevice(best);
      } catch (err) {
        console.error('Reconnect error:', err);
        setError(err?.message || String(err));
        return false;
      }
    },
    [getTransportId, openGrantedDevice, pairedDevices]
  );

  const retryPairedDeviceStatus = useCallback(
    async (transportId, vendorId, productId) => {
      if (!('hid' in navigator)) return false;
      try {
        const granted = await navigator.hid.getDevices();
        const knownEntry = pairedDevices.find(
          (entry) => entry.transportId === transportId
        );
        const best =
          knownEntry?.hidDevice && granted.includes(knownEntry.hidDevice)
            ? knownEntry.hidDevice
            : granted.find(
                (candidate) => getTransportId(candidate) === transportId
              ) || pickBestHidInterface(granted, { vendorId, productId });
        if (!best || !isReceiverTransport(vendorId, productId)) return false;
        const status = await probeReceiverDevice(best);
        return status != null;
      } catch (retryError) {
        console.warn('重新检测接收器失败:', retryError);
        return false;
      }
    },
    [getTransportId, pairedDevices, probeReceiverDevice]
  );

  /** 尝试自动连接最近一次使用的已授权设备 */
  const tryAutoConnect = useCallback(async () => {
    if (!('hid' in navigator) || isConnected || connectingRef.current) return false;
    const list = loadPairedDevices();
    if (list.length === 0) return false;
    try {
      const granted = await navigator.hid.getDevices();
      if (!granted?.length) return false;
      for (const remembered of list) {
        const best = pickBestHidInterface(granted, {
          vendorId: remembered.vendorId,
          productId: remembered.productId,
        });
        if (best && scoreHidInterface(best) >= 0) {
          return await openGrantedDevice(best);
        }
      }
    } catch (err) {
      console.warn('自动重连失败:', err);
    }
    return false;
  }, [isConnected, openGrantedDevice]);

  const disconnectDevice = useCallback(async () => {
    const current = deviceRef.current || device;
    connectionAttemptRef.current += 1;
    if (current) {
      detachTransportListener(current);
      try {
        if (current.opened) await current.close();
      } catch (err) {
        console.error('Disconnect error:', err);
      }
    }

    if (messageHandlerRef.current) {
      messageHandlerRef.current.clearBuffer();
    }
    if (senderRef.current) {
      senderRef.current.destroy();
      senderRef.current = null;
    }
    if (dataStoreRef.current) {
      dataStoreRef.current.clear();
    }
    macroPacketBufferRef.current = {
      packets: [],
      expectedPackets: 0,
      totalLength: 0,
      lastPacketAt: 0,
      restartCount: 0,
    };
    lightingPacketBufferRef.current = { payloads: [], firstPacket: null };

    deviceRef.current = null;
    deviceInfoRef.current = null;
    activeAdapterRef.current = null;
    suspendedRef.current = false;
    resumingRef.current = false;
    setDeviceSuspended(false);
    setSuspendNotice(null);
    setDevice(null);
    setIsConnected(false);
    setDriverReady(false);
    setConnectionPhase('idle');
    setTransportConnected(false);
    setConnectionType(null);
    setReceiverState(null);
    setTargetOnline(false);
    setTargetVid(null);
    setTargetPid(null);
    setActiveAdapter(null);
    setDeviceInfo(null);
    setProtocolVersion(null);
    setError(null);
    refreshOnlinePairedDevices();
    navigate('/', { replace: true });
  }, [
    detachTransportListener,
    device,
    navigate,
    refreshOnlinePairedDevices,
  ]);

  // 挂起期间定时探测接收器，等待设备被唤醒（唤醒上报丢失时的兜底）。
  useEffect(() => {
    if (!deviceSuspended) return undefined;
    const timer = window.setInterval(() => {
      probeSuspendedDevice();
    }, SUSPEND_POLL_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, [deviceSuspended, probeSuspendedDevice]);

  // 展示浏览器已授权设备。接收器目标离线时仍展示设备卡片，
  // 并继续监听状态上报，鼠标上线后再换成机型。
  // 注意：本 effect 的依赖链必须保持稳定（navigate 已经 ref 化）。它的
  // cleanup 会拆掉所有 inputreport 监听器，若依赖随导航变化而重跑，
  // 活动设备会出现监听空窗，宏多包读取的响应就会静默丢失。
  useEffect(() => {
    if (!('hid' in navigator)) return undefined;
    const transportInputHandlers = transportInputHandlersRef.current;
    const receiverProbePromises = receiverProbePromisesRef.current;

    // 防御性兜底：即使本 effect 因依赖变化重跑（cleanup 已拆除监听器），
    // 活动设备的监听器也要立刻挂回，不能依赖探测流程（探测会跳过活动设备）。
    if (deviceRef.current) {
      attachTransportListener(deviceRef.current);
    }

    refreshOnlinePairedDevices();

    const onConnect = () => {
      refreshOnlinePairedDevices();
    };

    const onDisconnect = (event) => {
      const lost = event?.device;
      refreshOnlinePairedDevices();

      if (
        lost &&
        deviceRef.current &&
        lost === deviceRef.current
      ) {
        console.log('设备已断开连接');
        detachTransportListener(deviceRef.current);
        clearActiveTarget('设备已断开，请重新连接');
        senderRef.current?.destroy();
        senderRef.current = null;
        deviceRef.current = null;
        setDevice(null);
        setTransportConnected(false);
        setConnectionType(null);
      }
    };

    try {
      navigator.hid.addEventListener('connect', onConnect);
      navigator.hid.addEventListener('disconnect', onDisconnect);
    } catch (_) {
      /* ignore */
    }
    return () => {
      try {
        navigator.hid.removeEventListener('connect', onConnect);
        navigator.hid.removeEventListener('disconnect', onDisconnect);
      } catch (_) {
        /* ignore */
      }
      for (const [hidDevice, handler] of transportInputHandlers) {
        try {
          hidDevice.removeEventListener('inputreport', handler);
        } catch (_) {
          /* ignore */
        }
      }
      transportInputHandlers.clear();
      receiverProbePromises.clear();
    };
  }, [
    attachTransportListener,
    clearActiveTarget,
    detachTransportListener,
    refreshOnlinePairedDevices,
  ]);

  const deviceCards = useMemo(
    () => mergeDeviceHistory(pairedDevices, deviceHistory),
    [deviceHistory, pairedDevices]
  );

  const value = {
    device,
    isConnected,
    driverReady,
    deviceSuspended,
    suspendNotice,
    retrySuspendedDevice: probeSuspendedDevice,
    connectionPhase,
    transportConnected,
    connectionType,
    receiverState,
    targetOnline,
    targetVid,
    targetPid,
    activeAdapter,
    isConnecting,
    deviceInfo,
    protocolVersion,
    error,
    pairedDevices: deviceCards,
    connectDevice,
    connectPairedDevice,
    retryPairedDeviceStatus,
    tryAutoConnect,
    removePairedDevice,
    disconnectDevice,
    messageHandler: messageHandlerRef.current,
    sender: senderRef.current,
    dataStore: dataStoreRef.current,
  };

  return (
    <DeviceContext.Provider value={value}>
      {children}
    </DeviceContext.Provider>
  );
};
